import { useEffect, useRef, useState } from 'react';
import {
  measureDownlink,
  measureLatency,
  readBrowserDownlink,
} from '../../../services/network-quality/probe';
import {
  DOWNLINK_WINDOW,
  evaluateNetworkQuality,
  LATENCY_WINDOW,
  NETWORK_STATE,
  pushSample,
  resolveNetworkState,
} from '../../../services/network-quality/quality';
import { ENV } from '@config/env';
import { useNetworkQualityReadings } from '../../../zustand/network-quality-store';

// Latency: one pair of tiny HEADs this often. The first verdict needs
// LATENCY_MIN_SAMPLES of them, so it lands ~25s after the page opens.
const LATENCY_INTERVAL_MS = 10000;
const FIRST_PROBE_DELAY_MS = 5000;
// Speed: two samples close together for a first verdict, then rarely — ~7.5 MB
// an hour at most. Measured in every browser: Chromium's own estimate is read
// too, but it misses a link that just degraded (see evaluateNetworkQuality).
const DOWNLINK_WARMUP_INTERVAL_MS = 30000;
const DOWNLINK_INTERVAL_MS = 120000;
// The browser's `offline` event fires on every Wi-Fi handoff too, often for
// well under a second; only an outage that lasts this long is announced.
const OFFLINE_ANNOUNCE_DELAY_MS = 1500;

const isBrowserOfflineNow = () => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Periodically measures the patient's own connection and reports only the
 * verdict (NETWORK_STATE), `offline` included. Built to stay out of the
 * exercise's way:
 *   - nothing runs unless `enabled` (FEATURES.CONNECTION_STATUS);
 *   - probes are sequential, one at a time, low priority, and skipped while
 *     the tab is hidden or the browser already reports offline;
 *   - the speed download is also skipped while the exercise socket is not open
 *     (reconnecting, so the connection is busy) — only
 *     the tiny ping keeps going then, since it is what tells "offline" apart
 *     from "the service dropped";
 *   - the page re-renders only when the verdict flips, not on every sample.
 */
export default function useNetworkQuality({ enabled, isSocketOpen }) {
  const [quality, setQuality] = useState(NETWORK_STATE.checking);
  const [unreachablePings, setUnreachablePings] = useState(0);
  const [isBrowserOffline, setIsBrowserOffline] = useState(false);
  const isBusyRef = useRef(false);
  // Not paused for a buffering reference video: on a slow link the video is
  // buffering most of the time, so that would skip the measurement exactly
  // when it matters. One 256 KB sample every two minutes is a brief share.
  isBusyRef.current = !isSocketOpen;

  // Free and instant: the browser's own online/offline events.
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let timeoutId = null;
    const update = () => {
      window.clearTimeout(timeoutId);

      if (!isBrowserOfflineNow()) {
        setIsBrowserOffline(false);
        // Pings that failed while the device was offline say nothing about now.
        setUnreachablePings(0);
        return;
      }

      timeoutId = window.setTimeout(
        () => setIsBrowserOffline(isBrowserOfflineNow()),
        OFFLINE_ANNOUNCE_DELAY_MS
      );
    };

    update();
    window.addEventListener('online', update);
    window.addEventListener('offline', update);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    let isCancelled = false;
    let timeoutId = null;
    let latencySamples = [];
    let downlinkSamples = [];
    let previous = NETWORK_STATE.checking;
    let lastDownlinkAt = -Infinity;

    const canPing = () => document.visibilityState === 'visible' && !isBrowserOfflineNow();
    const canDownload = () => canPing() && !isBusyRef.current;

    const tick = async () => {
      if (canPing()) {
        const latency = await measureLatency();

        if (isCancelled) {
          return;
        }

        if (latency === null) {
          setUnreachablePings(count => count + 1);
        } else {
          setUnreachablePings(0);
          latencySamples = pushSample(latencySamples, latency, LATENCY_WINDOW);
        }

        const browserDownlink = readBrowserDownlink();
        const downlinkInterval =
          downlinkSamples.length < DOWNLINK_WINDOW
            ? DOWNLINK_WARMUP_INTERVAL_MS
            : DOWNLINK_INTERVAL_MS;

        if (
          latency !== null &&
          performance.now() - lastDownlinkAt >= downlinkInterval &&
          canDownload()
        ) {
          lastDownlinkAt = performance.now();
          const downlink = await measureDownlink();

          if (isCancelled) {
            return;
          }

          if (downlink !== null) {
            downlinkSamples = pushSample(downlinkSamples, downlink, DOWNLINK_WINDOW);
          }
        }

        const result = evaluateNetworkQuality({
          latencySamples,
          downlinkSamples,
          browserDownlinkMbps: browserDownlink,
          previous,
        });

        previous = result.state;
        setQuality(result.state);

        // Raw numbers for the developer network panel's live monitor.
        useNetworkQualityReadings.getState().record({
          state: result.state,
          latencyMs: latency,
          latencyMedianMs: result.latencyMs,
          downlinkMbps: result.downlinkMbps,
          browserDownlinkMbps: browserDownlink,
        });

        // `npm run dev` only: the raw numbers behind the verdict, for checking
        // the thresholds by hand. Never shown to the patient.
        if (ENV.isLocal) {
          console.warn('[network-quality]', {
            state: result.state,
            latencyMs: latency,
            latencyMedianMs: result.latencyMs,
            downlinkMbps: result.downlinkMbps,
            browserDownlinkMbps: browserDownlink,
            measuredDownlinkMbps: downlinkSamples,
          });
        }
      }

      if (!isCancelled) {
        timeoutId = window.setTimeout(tick, LATENCY_INTERVAL_MS);
      }
    };

    timeoutId = window.setTimeout(tick, FIRST_PROBE_DELAY_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [enabled]);

  if (!enabled) {
    return null;
  }

  return resolveNetworkState({ isBrowserOffline, unreachablePings, quality });
}
